import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { schedule, type ReviewGrade } from '@diana/shared'
import { api, type ReviewCard } from '../../lib/api.js'
import { useLocale } from '../../i18n/index.js'
import { Column } from '../../classroom/Shell.js'
import { ButtonLink, ErrorLine, Skeleton } from '../../classroom/ui.js'
import { Icon } from '../../classroom/icons.js'
import { Markdown } from './Markdown.js'
import { studentCopy } from './copy.js'
import { shellCopy } from './copyShell.js'

/**
 * Flashcards (Review.png): one card at a time — the Polish word, then on a
 * tap the translation and the example, then four buttons for how it went,
 * each saying when the card comes back. The scheduler in packages/shared
 * decides; the buttons only preview its answer. Space flips, 1–4 rate.
 */
export function ReviewPage() {
  const locale = useLocale()
  const t = studentCopy(locale)
  const r = shellCopy(locale).review
  const queryClient = useQueryClient()

  const status = useQuery({ queryKey: ['review', 'status'], queryFn: api.review.status })
  const due = useQuery({ queryKey: ['review', 'due'], queryFn: api.review.due, refetchOnMount: 'always' })

  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [done, setDone] = useState(0)

  const grade = useMutation({
    mutationFn: ({ id, grade }: { id: string; grade: ReviewGrade }) => api.review.grade(id, grade),
    onSuccess: () => {
      setDone((d) => d + 1)
      setFlipped(false)
      setIndex((i) => i + 1)
      queryClient.invalidateQueries({ queryKey: ['review', 'status'] })
      queryClient.invalidateQueries({ queryKey: ['student', 'me'] })
    },
  })

  const cards = due.data?.cards ?? []
  const card: ReviewCard | undefined = cards[index]
  const total = Math.max(status.data?.due ?? 0, cards.length)
  const finished = !card && cards.length > 0

  const rate = (g: ReviewGrade) => {
    if (card && flipped && !grade.isPending) grade.mutate({ id: card.id, grade: g })
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!card || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (e.key === ' ') {
        e.preventDefault()
        setFlipped((f) => !f)
      } else if (flipped && ['1', '2', '3', '4'].includes(e.key)) {
        rate((Number(e.key) - 1) as ReviewGrade)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const restart = () => {
    setIndex(0)
    setDone(0)
    setFlipped(false)
    queryClient.invalidateQueries({ queryKey: ['review', 'due'] })
  }

  const gloss = (c: ReviewCard) => (locale === 'ru' ? c.ru : c.uk)
  const when = (c: ReviewCard, g: ReviewGrade) =>
    r.when(schedule({ ease: c.ease, intervalDays: c.intervalDays, reps: c.reps, lapses: c.lapses }, g, due.data?.today ?? c.dueOn).intervalDays)

  return (
    <Column
      topOnPhone
      top={
        <>
          <Link to="/panel" className="cl-link-quiet inline-flex min-h-11 items-center gap-1.5">
            <Icon name="back" size={18} />
            {r.end}
          </Link>
          {card && (
            <>
              <div
                role="progressbar"
                aria-label={r.progress}
                className="mx-auto h-1.5 max-w-[440px] flex-1 overflow-hidden rounded-[3px] bg-class-white"
              >
                <div className="h-full bg-class-gold transition-[width]" style={{ width: `${(index / cards.length) * 100}%` }} />
              </div>
              <span className="whitespace-nowrap text-class-gloss text-class-ink-faint">{r.counter(index + 1, cards.length, total)}</span>
            </>
          )}
        </>
      }
      bottom={
        card && (
          <p className="m-0 hidden w-full justify-center text-center text-class-gloss text-class-ink-faint desk:block [&_strong]:font-semibold [&_strong]:text-class-ink-soft">
            <Markdown variant="classroom">{r.keys}</Markdown>
          </p>
        )
      }
    >
      <main className="mx-auto flex max-w-[600px] flex-col gap-7 px-5 pt-6 pb-10 desk:px-12 desk:py-14">
        {due.isPending || status.isPending ? (
          <Skeleton lines={4} />
        ) : due.isError ? (
          <ErrorLine>{t.errors.load}</ErrorLine>
        ) : status.data?.total === 0 ? (
          <Done
            title={r.empty}
            text={r.emptyHint}
            action={
              <ButtonLink to="/panel/slownik">
                {shellCopy(locale).nav.glossary}
              </ButtonLink>
            }
          />
        ) : finished || !card ? (
          <Done
            title={done > 0 ? r.doneToday : r.nothingDue}
            text={done > 0 ? r.summary(done) : r.comeBack}
            action={
              <>
                {done > 0 && (status.data?.due ?? 0) > 0 && (
                  <button type="button" onClick={restart} className="cl-link min-h-[46px] px-2">
                    {r.restart}
                  </button>
                )}
                <ButtonLink to="/panel">{r.home}</ButtonLink>
              </>
            }
          />
        ) : (
          <>
            <button
              type="button"
              onClick={() => setFlipped((f) => !f)}
              aria-label={r.tap}
              className="flex min-h-[360px] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-class-lg border-0 bg-class-white px-8 py-12 text-center font-[inherit] text-inherit shadow-[inset_0_0_0_1px_var(--color-class-hairline)]"
            >
              <span className="text-class-label font-semibold text-class-ink-faint">{r.lesson(card.courseLevel, card.lessonNumber)}</span>
              <span lang="pl" className="mt-3 font-class-display text-class-title leading-tight">
                {card.term}
              </span>
              {card.pos && <span className="text-class-gloss text-class-ink-soft">{card.pos}</span>}
              {flipped ? (
                <span className="mt-6 flex w-full flex-col items-center gap-1.5 border-t border-class-line pt-6">
                  <span className="text-class-heading font-semibold">{gloss(card)}</span>
                  {locale === 'pl' && card.ru && <span className="text-class-gloss text-class-ink-soft">RU · {card.ru}</span>}
                  {card.example && (
                    <span lang="pl" className="mt-2 font-class-display italic text-class-ink-soft">
                      {card.example.replace(/[*_]/g, '')}
                    </span>
                  )}
                </span>
              ) : (
                <span className="mt-7 text-class-gloss text-class-ink-faint">{r.tap}</span>
              )}
            </button>

            {flipped && (
              <div className="grid grid-cols-2 gap-3 desk:grid-cols-4">
                {(
                  [
                    [0, r.again],
                    [1, r.hard],
                    [2, r.good],
                    [3, r.easy],
                  ] as const
                ).map(([g, label]) => (
                  <button
                    key={g}
                    type="button"
                    disabled={grade.isPending}
                    onClick={() => rate(g)}
                    className="flex min-h-[60px] cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[14px] border-0 bg-class-white font-[inherit] leading-tight font-semibold text-class-ink shadow-[inset_0_0_0_1px_var(--color-class-sky-200)] hover:shadow-[inset_0_0_0_1px_var(--color-class-sky-700)] disabled:opacity-60"
                  >
                    {label}
                    <span className="text-class-label font-normal text-class-ink-faint">{when(card, g)}</span>
                  </button>
                ))}
              </div>
            )}
            {grade.isError && <ErrorLine>{t.errors.load}</ErrorLine>}
          </>
        )}
      </main>
    </Column>
  )
}

function Done({ title, text, action }: { title: string; text: string; action: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2.5 rounded-class-lg bg-class-white px-8 py-14 text-center shadow-[inset_0_0_0_1px_var(--color-class-hairline)]">
      <span className="inline-flex h-[52px] w-[52px] items-center justify-center rounded-full bg-class-field-soft text-class-subheading font-semibold text-class-sky-700">
        ✓
      </span>
      <p className="cl-heading m-0">{title}</p>
      <p className="m-0 text-class-ink-soft">{text}</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-5">{action}</div>
    </div>
  )
}
